/**
 * Asking the page preview for an outline of the vendor's page.
 *
 * The form (where the vendor describes the spot) and the preview (which holds
 * the frame with their page and our placer script in it) are separate
 * components. They meet on two window events rather than through props, so
 * neither has to know how the other is built: the form asks, the preview
 * relays the question into its frame and the placer's answer back out.
 */

export const OUTLINE_REQUEST_EVENT = 'ardealy:outline-request';
export const OUTLINE_EVENT = 'ardealy:outline';

export interface PageOutline {
    nodes: any[];
    viewport?: { width: number; height: number };
    /**
     * Why there are no nodes: `no-page` when the preview has nothing loaded,
     * `no-answer` when a page is there but its script never replied — a placer
     * or SDK older than this feature (the live view runs the SDK the shop
     * serves, and a tab keeps the placer it first loaded).
     */
    missing?: 'no-page' | 'no-answer';
}

/** How long to wait for the page before saying it has not loaded. */
export const OUTLINE_TIMEOUT_MS = 5000;

/**
 * The outline of whatever page the preview is showing. Resolves with an empty
 * list when there is no page to read — no preview, or one still loading — so
 * the caller can say so rather than hang.
 */
export function requestPageOutline(timeoutMs = OUTLINE_TIMEOUT_MS): Promise<PageOutline> {
    const requestId = `o${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    return new Promise((resolve) => {
        const done = (outline: PageOutline) => {
            window.removeEventListener(OUTLINE_EVENT, onOutline as EventListener);
            window.clearTimeout(timer);
            resolve(outline);
        };
        const onOutline = (e: CustomEvent) => {
            if (e.detail?.requestId !== requestId) return;
            const nodes = Array.isArray(e.detail.nodes) ? e.detail.nodes : [];
            done({ nodes, viewport: e.detail.viewport, ...(nodes.length ? {} : { missing: 'no-page' as const }) });
        };
        const timer = window.setTimeout(() => done({ nodes: [], missing: 'no-answer' }), timeoutMs);
        window.addEventListener(OUTLINE_EVENT, onOutline as EventListener);
        window.dispatchEvent(new CustomEvent(OUTLINE_REQUEST_EVENT, { detail: { requestId } }));
    });
}
